import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "content", "title"]

  connect() {
    this.companyId = this.element.dataset.companyId
  }

  openSenpai(event) {
    event.preventDefault()
    this.openModal("senpai", "新入社員の声")
  }

  openJosi(event) {
    event.preventDefault()
    this.openModal("josi", "先輩社員の声")
  }

  openModal(type, title) {
    this.titleTarget.textContent = title
    this.modalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
    
    // コンテンツを読み込み
    this.loadEmployeeVoice(type)
  }

  close(event) {
    if (event) {
      event.preventDefault()
    }
    this.modalTarget.classList.add("hidden")
    document.body.style.overflow = "auto"
  }

  keydown(event) {
    if (event.key === "Escape") {
      this.close()
    }
  }

  async loadEmployeeVoice(type) {
    try {
      // 現在のURLから企業IDを取得
      const currentPath = window.location.pathname
      const companyId = currentPath.split('/').pop()
      
      // URLのパスに応じて適切なエンドポイントを選択
      let url
      if (currentPath.includes('/companies_search/')) {
        url = `/companies_search/${companyId}/employee_voice?voice_type=${type}`
      } else {
        // /company/ID 形式の場合
        url = `/company/${companyId}/employee_voice?voice_type=${type}`
      }
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      
      
      if (response.ok) {
        const html = await response.text()
        this.contentTarget.innerHTML = html
        
        // HTMLが読み込まれた後、Canvasによるスケジュール可視化を実行
        this.initializeScheduleChart()
      } else {
        console.error(`Failed to load data: ${response.status}`)
        const errorText = await response.text()
        console.error("Error response:", errorText)
        this.contentTarget.innerHTML = '<div class="p-6 text-center text-gray-500">データの読み込みに失敗しました。</div>'
      }
    } catch (error) {
      console.error('Error loading employee voice:', error)
      this.contentTarget.innerHTML = '<div class="p-6 text-center text-gray-500">データの読み込み中にエラーが発生しました。</div>'
    }
  }

  initializeScheduleChart() {
    
    // Canvasエレメントを検索
    const canvas = this.contentTarget.querySelector('#schedule-chart')
    if (!canvas) {
      return
    }
    
    // データ属性からスケジュール情報を取得
    const dataset = JSON.parse(canvas.dataset.dataset || '[]')
    const works = JSON.parse(canvas.dataset.works || '[]')
    const preData = JSON.parse(canvas.dataset.preData || '{}')
    
    
    // Canvas設定
    const ctx = canvas.getContext('2d')
    const canvasWidth = 800
    const canvasHeight = 400
    canvas.width = canvasWidth
    canvas.height = canvasHeight
    
    this.drawScheduleChart(ctx, dataset, works, preData, canvasWidth, canvasHeight)
  }

  drawScheduleChart(ctx, dataset, works, preData, width, height) {
    
    // 背景をクリア
    ctx.clearRect(0, 0, width, height)
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, width, height)
    
    // マージン設定
    const margin = { top: 40, right: 40, bottom: 60, left: 80 }
    const chartWidth = width - margin.left - margin.right
    const chartHeight = height - margin.top - margin.bottom
    
    // 時間軸（6:00 - 22:00）
    const startHour = 6
    const endHour = 22
    const hourRange = endHour - startHour
    
    // グリッド線を描画
    this.drawTimeGrid(ctx, margin, chartWidth, chartHeight, startHour, endHour)
    
    // スケジュールデータを処理
    if (dataset.length > 0) {
      this.drawScheduleBars(ctx, dataset, works, margin, chartWidth, chartHeight, startHour, hourRange)
    }
    
    // 凡例を描画
    this.drawLegend(ctx, works, width, height, margin)
  }

  drawTimeGrid(ctx, margin, chartWidth, chartHeight, startHour, endHour) {
    ctx.strokeStyle = '#e5e5e5'
    ctx.lineWidth = 1
    ctx.font = '12px Arial'
    ctx.fillStyle = '#666666'
    
    // 縦のグリッド線（時間軸）
    for (let hour = startHour; hour <= endHour; hour += 2) {
      const x = margin.left + ((hour - startHour) / (endHour - startHour)) * chartWidth
      
      ctx.beginPath()
      ctx.moveTo(x, margin.top)
      ctx.lineTo(x, margin.top + chartHeight)
      ctx.stroke()
      
      // 時間ラベル
      const timeLabel = `${hour}:00`
      const textWidth = ctx.measureText(timeLabel).width
      ctx.fillText(timeLabel, x - textWidth / 2, margin.top + chartHeight + 20)
    }
    
    // 横のグリッド線
    const dayLabels = ['月', '火', '水', '木', '金']
    for (let i = 0; i <= dayLabels.length; i++) {
      const y = margin.top + (i / dayLabels.length) * chartHeight
      
      ctx.beginPath()
      ctx.moveTo(margin.left, y)
      ctx.lineTo(margin.left + chartWidth, y)
      ctx.stroke()
      
      // 曜日ラベル
      if (i < dayLabels.length) {
        const labelY = y + chartHeight / dayLabels.length / 2 + 4
        ctx.fillText(dayLabels[i], margin.left - 20, labelY)
      }
    }
  }

  drawScheduleBars(ctx, dataset, works, margin, chartWidth, chartHeight, startHour, hourRange) {
    const dayHeight = chartHeight / 5 // 5日分
    const barHeight = dayHeight * 0.6
    const barMargin = dayHeight * 0.2
    
    // 色のパレット
    const colors = [
      '#3B82F6', // 青
      '#10B981', // 緑
      '#F59E0B', // オレンジ
      '#EF4444', // 赤
      '#8B5CF6', // 紫
      '#06B6D4', // シアン
      '#84CC16', // ライム
      '#F97316', // オレンジ
      '#EC4899'  // ピンク
    ]
    
    dataset.forEach((dayData, dayIndex) => {
      if (dayIndex >= 5) return // 5日分のみ表示
      
      const y = margin.top + dayIndex * dayHeight + barMargin
      
      // その日のスケジュールを描画
      for (let i = 1; i <= 9; i++) {
        const startTime = dayData[`s_time${i}`]
        const endTime = dayData[`e_time${i}`]
        const workType = dayData[`work${i}`]
        
        if (startTime && endTime && workType) {
          const startMinutes = this.timeToMinutes(startTime)
          const endMinutes = this.timeToMinutes(endTime)
          
          // 描画範囲内かチェック
          if (startMinutes >= startHour * 60 && endMinutes <= 22 * 60) {
            const startX = margin.left + ((startMinutes - startHour * 60) / (hourRange * 60)) * chartWidth
            const endX = margin.left + ((endMinutes - startHour * 60) / (hourRange * 60)) * chartWidth
            const barWidth = endX - startX
            
            // 作業タイプに応じた色を取得
            const workIndex = works.findIndex(work => work.value === workType)
            const color = colors[workIndex % colors.length]
            
            // バーを描画
            ctx.fillStyle = color
            ctx.fillRect(startX, y, barWidth, barHeight)
            
            // 枠線
            ctx.strokeStyle = '#ffffff'
            ctx.lineWidth = 1
            ctx.strokeRect(startX, y, barWidth, barHeight)
            
            // 時間ラベル（バーが十分大きい場合のみ）
            if (barWidth > 50) {
              ctx.fillStyle = '#ffffff'
              ctx.font = '10px Arial'
              const label = `${startTime}-${endTime}`
              const textWidth = ctx.measureText(label).width
              if (textWidth < barWidth - 4) {
                ctx.fillText(label, startX + (barWidth - textWidth) / 2, y + barHeight / 2 + 3)
              }
            }
          }
        }
      }
    })
  }

  drawLegend(ctx, works, width, height, margin) {
    const colors = [
      '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
      '#06B6D4', '#84CC16', '#F97316', '#EC4899'
    ]
    
    ctx.font = '12px Arial'
    ctx.fillStyle = '#333333'
    
    const legendStartY = height - margin.bottom + 35
    const legendItemWidth = 120
    const legendItemHeight = 20
    const legendCols = Math.floor((width - margin.left - margin.right) / legendItemWidth)
    
    works.forEach((work, index) => {
      const col = index % legendCols
      const row = Math.floor(index / legendCols)
      const x = margin.left + col * legendItemWidth
      const y = legendStartY + row * legendItemHeight
      
      // 色の四角形
      ctx.fillStyle = colors[index % colors.length]
      ctx.fillRect(x, y, 12, 12)
      
      // テキスト
      ctx.fillStyle = '#333333'
      ctx.fillText(work.label, x + 16, y + 9)
    })
  }

  timeToMinutes(timeString) {
    const [hours, minutes] = timeString.split(':').map(Number)
    return hours * 60 + minutes
  }
}
